import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// shadcn-style Button. cva handles variant + size. `asChild` lets you wrap
// a link or non-button element while keeping styling.

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium ring-offset-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:    'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary:  'bg-secondary text-secondary-foreground border border-border hover:bg-bg-elevated',
        ghost:      'text-fg-muted hover:bg-bg-elevated hover:text-fg',
        link:       'text-thermal-orange underline-offset-4 hover:underline',
        destructive:'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      size: {
        sm:  'h-8 px-3 text-xs',
        md:  'h-10 px-4',
        lg:  'h-12 px-6 text-base',
        icon:'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
  },
);
Button.displayName = 'Button';
